/* eslint-disable camelcase, no-param-reassign */
import React from 'react';
import {
  Formik,
  Field,
} from 'formik';
import {
  useDispatch,
} from 'react-redux';
import {
  Prompt,
  useHistory,
} from 'react-router-dom';
import i18n from 'i18next';

import {
  coreActions,
} from '@gymcloud/core';

import {
  useEventListener,
} from 'web-hooks';
import {
  decodeParam,
} from 'web-utils';

import {
  Video,
  Button,
  Author,
  TagList,
  Tooltip,
  TagField,
  TextField,
  EditorField,
  MarkdownRenderer,
} from 'web-components';

import {
  Flex,
  H100,
} from 'web-styled';

import * as S from './styled';

type Props = {
  isPro: boolean,
  entity: any,
  readOnly?: boolean,
  resourceType: string,
  onRequestUpdate: { (): void },
};

const FillEntity = ({
  isPro,
  entity,
  readOnly,
  resourceType,
  onRequestUpdate,
}: Props) => {
  let shouldBlockNavigation = false;
  const history = useHistory();
  const dispatch = useDispatch();

  useEventListener(
    'beforeunload',
    (e: Event) => {
      if (shouldBlockNavigation) {
        const message = 'Changes you made may not be saved.';
        e.returnValue = false;
        return message;
      }
      return '';
    },
  );

  const handleUpdate = (values, actions) => {
    const onSuccess = () => {
      const params = decodeParam(window.location.search);
      if (entity.exercise_id
              && (entity.workout_type === 'PersonalWorkout'
                  || entity.workout_type === 'WorkoutTemplate')) {
        dispatch(
          coreActions.requestUpdateEntity({
            id: entity.exercise_id,
            payload: values,
            resourceType: 'exercises',
          }),
        );
      }

      if (params.back) {
        history.push({
          pathname: params.back,
          state: { entity },
        });
      } else if (resourceType === 'exercises') {
        history.push(`/exercises/${entity.id}/overview`);
      } else {
        history.goBack();
      }
    };

    dispatch(
      coreActions.requestUpdateEntity({
        id: entity.id,
        payload: values,
        resourceType,
        callbacks: {
          ...actions,
          onSuccess,
        },
      }),
    );
  };

  const hasTag = entity?.tag_list !== undefined;

  return (
    <Formik
      initialValues={{
        name: entity?.name || '',
        tag_list: hasTag ? entity?.tag_list.join(',') : undefined,
        description: entity?.description || '',
      }}
      onSubmit={onRequestUpdate || handleUpdate}
    >
      {(formikProps) => (
        <S.Container
          onKeyPress={() => {
            shouldBlockNavigation = true;
          }}
          spacing={30}
          onSubmit={(e: any) => {
            e.preventDefault();
            formikProps.handleSubmit(e);
          }}
        >
          <S.Header>
            <Flex align="center">
              {entity?.author_full_name && (
                <Author
                  fullName={entity?.author_full_name}
                />
              )}
            </Flex>
            <Button
              id="formSubmitButton"
              type="submit"
              style={{ visibility: 'hidden' }}
            />
          </S.Header>
          <S.Content>
            <Flex
              column
              flex="0 0 50%"
              align="center"
            >
              <Video
                id={entity.video_id}
                src={entity.video_url}
                title={entity?.name}
                entityId={entity?.id}
                readOnly={readOnly}
                resourceType={resourceType}
                height="400px"
              />
            </Flex>
            {readOnly ? (
              <Flex
                column
                spacing={30}
                flex="1 1 auto"
                padding="0 0 30px 0"
              >
                <H100 color="primary">
                  {entity.name}
                </H100>

                <MarkdownRenderer
                  source={entity?.description}
                  placeholder={isPro
                    ? i18n.t('click.to.add.description', { type: 'exercise' })
                    : null}
                />

                <TagList
                  tags={entity.tag_list}
                  resourceType={resourceType}
                />
              </Flex>
            ) : (
              <Flex
                column
                spacing={30}
                flex="1 1 auto"
                padding="0 0 30px 0"
              >
                <Field
                  flex="0 0 60px"
                  name="name"
                  label="Name"
                  color="primary"
                  component={TextField}
                />
                <Tooltip
                  body="Type the Exercise description here"
                  preferPlace="above"
                >
                  <Field
                    name="description"
                    color="primary"
                    label="Description"
                    placeholder="Type exercise description here..."
                    component={EditorField}
                  />
                </Tooltip>
                <Field
                  name="tag_list"
                  component={TagField}
                />
              </Flex>
            )}
          </S.Content>
          <Prompt
            when={shouldBlockNavigation}
            message="Are you sure you want to leave without saving?"
          />
        </S.Container>
      )}
    </Formik>
  );
};

FillEntity.defaultProps = {
  readOnly: false,
};

export default FillEntity;
